import React, { useEffect, useMemo, useState } from "react";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputSwitch } from "primereact/inputswitch";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { getGrades } from "../../api/gradeApi";
import { getSubjects } from "../../api/subjectApi";
import {
  getCurriculumSubjects,
  editCurriculumSubject,
  deleteCurriculumSubject,
  bulkSetGradeCurriculum,
} from "../../api/curriculumSubjectsApi";
import DynamicDataTable from "../../common/components/Common/DataListing";

const GradeCurriculumManager = () => {
  const [gradeId, setGradeId] = useState(null);
  const [grades, setGrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(false);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [current, setCurrent] = useState(null);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedSubjectId, setSelectedSubjectId] = useState(null);
  const [bulkAddIds, setBulkAddIds] = useState([]);
  const [copyFromGradeId, setCopyFromGradeId] = useState(null);
  const [isMandatory, setIsMandatory] = useState(true);
  const [editSubject, setEditSubject] = useState(null);
  const [isSubjectDialogVisible, setIsSubjectDialogVisible] = useState(false);

  // Dropdown options
  const subjectOptions = useMemo(
    () =>
      (subjects || []).map((s) => ({
        label: `${s.name}${s.code ? ` (${s.code})` : ""}`,
        value: s.id,
      })),
    [subjects]
  );

  // Load data
  const loadGrades = async () => {
    try {
      const data = await getGrades();
      setGrades(Array.isArray(data) ? data : data?.data || []);
    } catch {
      toast.error("Failed to load grades");
    }
  };

  const loadSubjects = async () => {
    try {
      const res = await getSubjects({ is_active: true });
      setSubjects(res.data || []);
    } catch {
      toast.error("Failed to load subjects");
    }
  };

  const loadAllMappings = async () => {
    setLoading(true);
    try {
      const res = await getCurriculumSubjects({});
      setMappings(res.data || []);
    } catch {
      toast.error("Failed to load curriculum");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGrades();
    loadSubjects();
    loadAllMappings();
  }, []);

  // Toggle Mandatory/Elective
  const toggleType = async (row, mandatory) => {
    try {
      const res = await editCurriculumSubject(row.id, {
        is_mandatory: !!mandatory,
        is_elective: !mandatory,
        group: row.group || null,
        order: row.order || null,
      });
      if (res.success === false)
        return toast.error(res.message || "Update failed");
      toast.success("Updated");
      loadAllMappings();
    } catch {
      toast.error("Update failed");
    }
  };

  // Delete grade mapping row from table
  const removeRow = async (row) => {
    try {
      const id = typeof row === "object" ? row.id : row;
      if (!id) return;
      const res = await deleteCurriculumSubject(id);
      if (res.success === false)
        return toast.error(res.message || "Remove failed");
      toast.success("Removed successfully");
      loadAllMappings();
    } catch {
      toast.error("Remove failed");
    }
  };

  // Edit grade
  const onEdit = (row) => {
    setIsEditing(true);
    setCurrent({
      grade_id: row.grade_id ?? row.grade?.id ?? gradeId,
    });

    const gradeSubjects = mappings.filter(
      (m) => m.grade_id === (row.grade_id ?? row.grade?.id)
    );
    setSelectedSubjects(
      gradeSubjects.map((subject) => ({
        id: subject.id, // important for delete
        subject_id: subject.subject_id ?? subject.subject?.id,
        subject_name: subject.subject?.name || "",
        subject_code: subject.subject?.code || "",
        is_mandatory: !!subject.is_mandatory,
        is_elective: !!subject.is_elective,
      }))
    );

    setIsModalVisible(true);
  };

  // Add new grade
  const onCreate = () => {
    setIsEditing(false);
    setCurrent({ grade_id: gradeId ?? null });
    setSelectedSubjects([]);
    setSelectedSubjectId(null);
    setIsMandatory(true);
    setIsModalVisible(true);
  };

  // Add subject to grade
  const addSubjectToGrade = () => {
    if (!selectedSubjectId) {
      toast.warn("Please select a subject first.");
      return;
    }

    const subject = subjects.find((s) => s.id === selectedSubjectId);
    if (!subject) {
      toast.error("Subject not found.");
      return;
    }

    const isAlreadyAdded = selectedSubjects.some(
      (s) => s.subject_id === subject.id
    );
    if (isAlreadyAdded) {
      toast.warn("This subject is already added to the grade.");
      return;
    }

    setSelectedSubjects([
      ...selectedSubjects,
      {
        subject_id: subject.id,
        subject_name: subject.name,
        subject_code: subject.code || "",
        is_mandatory: isMandatory,
        is_elective: !isMandatory,
      },
    ]);

    setSelectedSubjectId(null);
    setIsMandatory(true);
  };

  // Add several subjects at once (default mandatory flag from the switch)
  const addBulkSubjects = () => {
    if (!bulkAddIds.length) return;
    setSelectedSubjects((prev) => {
      const have = new Set(prev.map((s) => s.subject_id));
      const additions = bulkAddIds
        .filter((id) => !have.has(id))
        .map((id) => {
          const subject = subjects.find((s) => s.id === id);
          return subject
            ? {
                subject_id: subject.id,
                subject_name: subject.name,
                subject_code: subject.code || "",
                is_mandatory: isMandatory,
                is_elective: !isMandatory,
              }
            : null;
        })
        .filter(Boolean);
      return [...prev, ...additions];
    });
    setBulkAddIds([]);
  };

  // Load another grade's subjects into the current selection (staged, saved on submit)
  const copyFromGrade = () => {
    if (!copyFromGradeId) {
      toast.warn("Pick a grade to copy from.");
      return;
    }
    const sourceRows = mappings.filter(
      (m) => (m.grade_id ?? m.grade?.id) === copyFromGradeId
    );
    if (!sourceRows.length) {
      toast.info("That grade has no subjects to copy.");
      return;
    }
    setSelectedSubjects((prev) => {
      const have = new Set(prev.map((s) => s.subject_id));
      const additions = sourceRows
        .map((m) => ({
          subject_id: m.subject_id ?? m.subject?.id,
          subject_name: m.subject?.name || "",
          subject_code: m.subject?.code || "",
          is_mandatory: !!m.is_mandatory,
          is_elective: !m.is_mandatory,
        }))
        .filter((s) => s.subject_id && !have.has(s.subject_id));
      return [...prev, ...additions];
    });
    const srcName = grades.find((g) => g.id === copyFromGradeId)?.name || "grade";
    toast.success(`Copied subjects from ${srcName}. Review, then save.`);
    setCopyFromGradeId(null);
  };

  // Remove subject from the staged selection (persisted on Save via bulk replace)
  const removeSubjectFromGrade = (subjectId) => {
    setSelectedSubjects((prev) => prev.filter((s) => s.subject_id !== subjectId));
  };

  // Open edit subject dialog
  const openEditSubject = (subject) => {
    setEditSubject({ ...subject });
    setIsSubjectDialogVisible(true);
  };

  // Save single subject edit
  const saveEditSubject = async () => {
    if (!editSubject) return;
    const updated = { ...editSubject, is_elective: !editSubject.is_mandatory };

    if (isEditing && updated.id) {
      try {
        const payload = {
          grade_id: current.grade_id,
          subject_id: updated.subject_id,
          is_mandatory: !!updated.is_mandatory,
          is_elective: !!updated.is_elective,
          group: null,
          order: null,
        };
        const res = await editCurriculumSubject(updated.id, payload);
        if (res?.success === false)
          return toast.error(res.message || "Update failed");
        toast.success("Updated successfully");
        setSelectedSubjects((prev) =>
          prev.map((s) =>
            s.subject_id === updated.subject_id ? updated : s
          )
        );
        loadAllMappings();
      } catch {
        toast.error("Update failed");
      }
    } else {
      setSelectedSubjects((prev) =>
        prev.map((s) => (s.subject_id === updated.subject_id ? updated : s))
      );
    }
    setIsSubjectDialogVisible(false);
    setEditSubject(null);
  };

  // Submit grade subjects (add/edit)
  const onSubmit = async () => {
    if (!current?.grade_id) {
      toast.warn("Please select a grade.");
      return;
    }
    if (selectedSubjects.length === 0) {
      toast.warn("Please add at least one subject.");
      return;
    }

    setLoading(true);
    try {
      // One atomic call — the grade's curriculum becomes exactly this list
      // (adds new, updates mandatory/elective, removes any that were taken out).
      const res = await bulkSetGradeCurriculum(
        current.grade_id,
        selectedSubjects.map((s) => ({
          subject_id: s.subject_id,
          is_mandatory: !!s.is_mandatory,
        })),
        "replace"
      );

      if (res?.success === false) {
        toast.error(res.message || "Save failed");
      } else {
        toast.success(res?.message || "Curriculum saved");
      }

      setIsModalVisible(false);
      setCurrent(null);
      setSelectedSubjects([]);
      setSelectedSubjectId(null);
      setBulkAddIds([]);
      setCopyFromGradeId(null);
      setIsMandatory(true);
      setGradeId(current.grade_id);
      loadAllMappings();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setLoading(false);
    }
  };

  // Group table data
  const groupedMappings = useMemo(() => {
    const grouped = {};
    mappings.forEach((mapping) => {
      const gid = mapping.grade_id ?? mapping.grade?.id;
      const gname = mapping.grade?.name ?? `Grade ${gid}`;
      if (!grouped[gid]) {
        grouped[gid] = {
          grade_id: gid,
          grade_name: gname,
          subjects: [],
          mandatory_count: 0,
          elective_count: 0,
          total_subjects: 0,
        };
      }
      grouped[gid].subjects.push(mapping);
      grouped[gid].total_subjects++;
      if (mapping.is_mandatory) grouped[gid].mandatory_count++;
      else grouped[gid].elective_count++;
    });
    return Object.values(grouped);
  }, [mappings]);

  const columns = useMemo(
    () => [
      { header: "Grade", body: (row) => row.grade_name },
      { header: "Total Subjects", body: (row) => row.total_subjects },
      { header: "Mandatory", body: (row) => row.mandatory_count },
      { header: "Elective", body: (row) => row.elective_count },
    ],
    []
  );

  return (
    <div className="p-3">
      <ToastContainer />
      <DynamicDataTable
        data={groupedMappings}
        subject="Grade Curriculum"
        permissionSubject="Curriculum"
        columns={columns}
        onCreate={onCreate}
        onEdit={onEdit}
        onDelete={removeRow}
        loading={loading}
      />

      {/* Add/Edit Modal */}
      <Dialog
        header={isEditing ? "Edit Grade Subjects" : "Add Grade Subjects"}
        visible={isModalVisible}
        style={{ width: "50rem" }}
        onHide={() => {
          setIsModalVisible(false);
          setSelectedSubjects([]);
          setCurrent(null);
          setSelectedSubjectId(null);
          setIsMandatory(true);
        }}
      >
        <div className="p-fluid">
          <div className="p-field mb-3">
            <label>Grade</label>
            <Dropdown
              value={current?.grade_id ?? null}
              options={grades.map((g) => ({ label: g.name, value: g.id }))}
              onChange={(e) => setCurrent({ ...current, grade_id: e.value })}
              placeholder="Select Grade"
              disabled={isEditing}
            />
          </div>

          {/* Copy an existing grade's curriculum as a starting point */}
          {current?.grade_id && (
            <div className="p-field mb-3">
              <label>Copy from another grade</label>
              <div className="flex align-items-center gap-2 p-3 border-1 border-300 border-round surface-50">
                <Dropdown
                  value={copyFromGradeId}
                  options={grades
                    .filter((g) => g.id !== current?.grade_id)
                    .map((g) => ({ label: g.name, value: g.id }))}
                  onChange={(e) => setCopyFromGradeId(e.value)}
                  placeholder="Select source grade"
                  filter
                  showClear
                  className="w-6"
                />
                <Button
                  label="Copy Subjects"
                  icon="pi pi-copy"
                  className="p-button-help"
                  onClick={copyFromGrade}
                  disabled={!copyFromGradeId}
                />
              </div>
            </div>
          )}

          {/* Default mandatory/elective flag applied to newly added subjects */}
          <div className="p-field mb-3">
            <label>Add Subjects to Grade</label>
            <div className="flex flex-column gap-2 p-3 border-1 border-300 border-round">
              <div className="flex align-items-center gap-2">
                <span className="text-sm font-medium">New subjects are</span>
                <span className="text-sm">{isMandatory ? "Mandatory" : "Elective"}</span>
                <InputSwitch checked={isMandatory} onChange={(e) => setIsMandatory(e.value)} />
              </div>

              {/* Bulk add — pick many at once */}
              <div className="flex align-items-center gap-2">
                <MultiSelect
                  value={bulkAddIds}
                  options={subjectOptions.filter(
                    (o) => !selectedSubjects.some((s) => s.subject_id === o.value)
                  )}
                  onChange={(e) => setBulkAddIds(e.value)}
                  placeholder="Select subjects to add"
                  filter
                  display="chip"
                  className="w-8"
                />
                <Button
                  label="Add Selected"
                  icon="pi pi-plus"
                  className="p-button-success"
                  onClick={addBulkSubjects}
                  disabled={!bulkAddIds.length}
                />
              </div>

              {/* Single add — kept for quick one-off */}
              <div className="flex align-items-center gap-2">
                <Dropdown
                  value={selectedSubjectId}
                  options={subjectOptions.filter(
                    (o) => !selectedSubjects.some((s) => s.subject_id === o.value)
                  )}
                  onChange={(e) => setSelectedSubjectId(e.value)}
                  placeholder="…or add one"
                  filter
                  filterBy="label"
                  showClear
                  className="w-8"
                />
                <Button
                  label="Add"
                  icon="pi pi-plus"
                  className="p-button-outlined"
                  onClick={addSubjectToGrade}
                  disabled={!selectedSubjectId}
                />
              </div>
            </div>
          </div>

          <div className="p-field mb-3">
            <label>Selected Subjects for Grade</label>
            <div className="border-1 border-300 border-round p-3" style={{ minHeight: "150px" }}>
              {selectedSubjects.length === 0 ? (
                <div className="text-center text-gray-500 p-4">
                  No subjects selected. Add subjects from the list above.
                </div>
              ) : (
                selectedSubjects.map((subject) => (
                  <div key={subject.subject_id} className="flex align-items-center justify-content-between mb-2 p-2 border-round bg-blue-50">
                    <div className="flex align-items-center gap-2">
                      <span className="font-medium">
                        {subject.subject_name}
                        {subject.subject_code && ` (${subject.subject_code})`}
                      </span>
                      <span className={`text-xs px-2 py-1 border-round ${subject.is_mandatory ? "bg-green-100 text-green-800" : "bg-orange-100 text-orange-800"}`}>
                        {subject.is_mandatory ? "Mandatory" : "Elective"}
                      </span>
                    </div>
                    <div className="flex align-items-center gap-1">
                      <Button icon="pi pi-pencil" className="p-button-sm p-button-text p-button-info" onClick={() => openEditSubject(subject)} tooltip="Edit Subject" />
                      <Button icon="pi pi-times" className="p-button-sm p-button-danger p-button-text" onClick={() => removeSubjectFromGrade(subject.subject_id)} tooltip="Remove Subject" />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="mt-3 flex justify-content-end gap-2">
            <Button label="Cancel" onClick={() => { setIsModalVisible(false); setSelectedSubjects([]); setCurrent(null); setSelectedSubjectId(null); setIsMandatory(true); }} className="p-button-secondary" />
            <Button label={isEditing ? "Update Grade Subjects" : "Add Grade Subjects"} onClick={onSubmit} className="p-button-primary" disabled={selectedSubjects.length === 0} />
          </div>
        </div>
      </Dialog>

      {/* Edit single subject dialog */}
      <Dialog
        header="Edit Subject"
        visible={isSubjectDialogVisible}
        style={{ width: "25rem" }}
        onHide={() => { setIsSubjectDialogVisible(false); setEditSubject(null); }}
      >
        <div className="p-fluid">
          <div className="p-field mb-3">
            <label>Subject</label>
            <InputText value={`${editSubject?.subject_name || ""}${editSubject?.subject_code ? ` (${editSubject.subject_code})` : ""}`} disabled />
          </div>
          <div className="p-field mb-3 flex align-items-center gap-2">
            <label className="text-sm font-medium">Mandatory</label>
            <InputSwitch checked={!!editSubject?.is_mandatory} onChange={(e) => setEditSubject(prev => ({ ...prev, is_mandatory: e.value }))} />
          </div>
          <div className="flex justify-content-end gap-2 mt-3">
            <Button label="Cancel" className="p-button-secondary" onClick={() => { setIsSubjectDialogVisible(false); setEditSubject(null); }} />
            <Button label="Save" className="p-button-primary" onClick={saveEditSubject} />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default GradeCurriculumManager;
